import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { OrganizationUser } from "schemas/dashboard";

import Alert from "components/Alert";
import { convertPlace } from "features/Influencer/Local/CheckoutDialog/utils";
import { MapListItem } from "features/Influencer/Local/MapListItem";
import { GooglePlaceResult } from "features/Influencer/Local/schema";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

interface Niche {
  id: number;
  name: string;
  isArchived: boolean;
  status: string;
  locationName?: string;
}

interface OrgNicheCache {
  niches: Niche[];
  loading: boolean;
}

interface SelectedOrg {
  organizationId: number;
  name: string;
  email: string;
  collectionName: string;
}

interface PitchDialogProps {
  open: boolean;
  handleClose: () => void;
  onSuccess: (openLocationDialog: boolean) => void;
  addedPlaces: GooglePlaceResult[];
  addPlace: (place: GooglePlaceResult) => void;
  searchQuery: string;
}

type Step = "select" | "confirm" | "complete";

export default function PitchDialog({
  open,
  handleClose,
  onSuccess,
  addedPlaces,
  addPlace,
  searchQuery,
}: PitchDialogProps) {
  const { getToken } = useAuth();
  const [message, severity, setAlert, closeAlert] = useAlert();

  const [step, setStep] = useState<Step>("select");

  const [orgUsers, setOrgUsers] = useState<OrganizationUser[]>([]);
  const [orgSearchLoading, setOrgSearchLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const [selectedOrgs, setSelectedOrgs] = useState<SelectedOrg[]>([]);
  const [orgNiches, setOrgNiches] = useState<Record<number, OrgNicheCache>>({});
  const [selectedNicheIds, setSelectedNicheIds] = useState<
    Record<number, number | null>
  >({});

  const [submitLoading, setSubmitLoading] = useState(false);

  // Reset step when dialog opens
  useEffect(() => {
    if (open) {
      setStep("select");
    }
  }, [open]);

  const fetchOrgUsers = async (query?: string) => {
    setOrgSearchLoading(true);
    try {
      const { organizationUsers } = await fetcherAuth(
        getToken,
        query
          ? `/api/admin/organization-users?query=${query}`
          : "/api/admin/organization-users",
        "GET",
      );
      setOrgUsers(organizationUsers);
    } catch {
      setAlert("Failed to fetch organizations", "warning");
    }
    setOrgSearchLoading(false);
  };

  useEffect(() => {
    if (open) {
      fetchOrgUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (inputValue) {
        fetchOrgUsers(inputValue);
      } else {
        fetchOrgUsers();
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const fetchNichesForOrg = useCallback(
    async (orgId: number) => {
      setOrgNiches((prev) => ({
        ...prev,
        [orgId]: { niches: prev[orgId]?.niches || [], loading: true },
      }));
      try {
        const res = await fetcherAuth(
          getToken,
          `/api/admin/virtual-assistant-users/${orgId}/niches`,
          "GET",
        );
        setOrgNiches((prev) => ({
          ...prev,
          [orgId]: { niches: res.niches || [], loading: false },
        }));
      } catch {
        setOrgNiches((prev) => ({
          ...prev,
          [orgId]: { niches: [], loading: false },
        }));
      }
    },
    [getToken],
  );

  const handleOrgSelect = (_: unknown, newValue: OrganizationUser | null) => {
    if (!newValue) return;
    const alreadySelected = selectedOrgs.some(
      (o) => o.organizationId === newValue.organizationId,
    );
    if (alreadySelected) return;

    const newOrg: SelectedOrg = {
      organizationId: newValue.organizationId,
      name: newValue.name,
      email: newValue.email,
      collectionName: "",
    };
    setSelectedOrgs((prev) => [...prev, newOrg]);

    if (!orgNiches[newOrg.organizationId]) {
      fetchNichesForOrg(newOrg.organizationId);
    }
  };

  const handleRemoveOrg = (orgId: number) => {
    setSelectedOrgs((prev) => prev.filter((o) => o.organizationId !== orgId));
    setSelectedNicheIds((prev) => {
      const next = { ...prev };
      delete next[orgId];
      return next;
    });
  };

  const selectNiche = (orgId: number, nicheId: number) => {
    setSelectedNicheIds((prev) => ({
      ...prev,
      [orgId]: prev[orgId] === nicheId ? null : nicheId,
    }));
  };

  const updateCollectionName = (orgId: number, name: string) => {
    setSelectedOrgs((prev) =>
      prev.map((o) =>
        o.organizationId === orgId ? { ...o, collectionName: name } : o,
      ),
    );
  };

  const getOrgNicheEntries = () =>
    selectedOrgs
      .filter((org) => selectedNicheIds[org.organizationId])
      .map((org) => ({
        organizationNicheId: selectedNicheIds[org.organizationId]!,
        collectionName: org.collectionName || undefined,
      }));

  const getNicheName = (orgId: number, nicheId: number): string => {
    const niches = orgNiches[orgId]?.niches || [];
    const niche = niches.find((n) => n.id === nicheId);
    if (!niche) return `Niche #${nicheId}`;
    return (
      niche.name ||
      `Niche #${niche.id}` +
        (niche.locationName ? ` (${niche.locationName})` : "")
    );
  };

  const handleContinue = () => {
    const entries = getOrgNicheEntries();
    if (entries.length === 0) {
      setAlert(
        "You must select at least one organization and its niche",
        "warning",
      );
      return;
    }
    setStep("confirm");
  };

  const handleSubmit = async () => {
    const orgNicheEntries = getOrgNicheEntries();

    if (orgNicheEntries.length === 0) {
      setAlert(
        "You must select at least one organization and its niche",
        "warning",
      );
      setStep("select");
      return;
    }

    if (addedPlaces.length === 0) {
      setAlert("You must have at least one brand selected", "warning");
      return;
    }

    setSubmitLoading(true);
    try {
      await fetcherAuth(
        getToken,
        "/api/admin/google-place-brands/upload-local-places",
        "POST",
        {},
        {
          places: addedPlaces.map((place) => convertPlace(place)),
          organizationNiches: orgNicheEntries,
          searchMetadata: {
            text_query: searchQuery,
          },
        },
      );
      setStep("complete");
    } catch (error) {
      setAlert(error?.message || "An error occurred", "warning");
    } finally {
      setSubmitLoading(false);
    }
  };

  const renderSelectStep = () => (
    <>
      <DialogContent>
        <Typography color="textSecondary" sx={{ mb: 3 }}>
          {addedPlaces.length} brand{addedPlaces.length !== 1 ? "s" : ""}{" "}
          selected
          {searchQuery ? ` from "${searchQuery}"` : ""}
        </Typography>

        {/* Step 1: Add Organizations */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            1. Add organizations
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Search by name, email, or organization ID.
          </Typography>
          <Autocomplete
            disablePortal
            loading={orgSearchLoading}
            options={orgUsers}
            getOptionLabel={(option) =>
              `${option.name} - ${option.email} | ${option.organizationId}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search by name, email, or organization ID"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {orgSearchLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            onChange={handleOrgSelect}
            onInputChange={(_event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            value={null}
            blurOnSelect
          />
          {selectedOrgs.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                mt: 1,
              }}
            >
              {selectedOrgs.map((org) => (
                <Chip
                  key={org.organizationId}
                  label={`${org.name} - ${org.email}`}
                  onDelete={() => handleRemoveOrg(org.organizationId)}
                  size="small"
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Step 2: Select Niches + Optional Collection Name */}
        {selectedOrgs.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              2. Select a niche for each organization
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Choose which niche should receive the uploaded brands. You can
              optionally set a collection name per organization.
            </Typography>
            {selectedOrgs.map((org) => {
              const cache = orgNiches[org.organizationId];
              const niches = cache?.niches || [];
              return (
                <Box key={org.organizationId} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {org.name} - {org.email}
                  </Typography>
                  {cache?.loading ? (
                    <Typography variant="caption" color="text.secondary">
                      Loading niches...
                    </Typography>
                  ) : niches.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No niches found
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                      }}
                    >
                      {niches.map((niche) => {
                        const isSelected =
                          selectedNicheIds[org.organizationId] === niche.id;
                        const statusLabel = niche.isArchived
                          ? "archived"
                          : niche.status === "active"
                            ? "active"
                            : niche.status;
                        const nicheName =
                          niche.name ||
                          `Niche #${niche.id}` +
                            (niche.locationName
                              ? ` (${niche.locationName})`
                              : "");
                        return (
                          <Chip
                            key={niche.id}
                            label={`${nicheName} (${statusLabel})`}
                            disabled={
                              !(niche.status === "active" && !niche.isArchived)
                            }
                            onClick={() => {
                              if (
                                niche.status === "active" &&
                                !niche.isArchived
                              ) {
                                selectNiche(org.organizationId, niche.id);
                              }
                            }}
                            color={
                              isSelected
                                ? "primary"
                                : niche.status === "active" && !niche.isArchived
                                  ? "success"
                                  : "default"
                            }
                            variant={isSelected ? "filled" : "outlined"}
                            size="small"
                            sx={
                              niche.isArchived
                                ? {
                                    opacity: 0.5,
                                    textDecoration: "line-through",
                                  }
                                : undefined
                            }
                          />
                        );
                      })}
                    </Box>
                  )}
                  <TextField
                    size="small"
                    label="Collection name (optional)"
                    value={org.collectionName}
                    onChange={(e) =>
                      updateCollectionName(org.organizationId, e.target.value)
                    }
                    sx={{ mt: 1, width: "100%" }}
                  />
                </Box>
              );
            })}
          </Box>
        )}

        <Alert message={message} severity={severity} closeAlert={closeAlert} />
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={handleContinue}>
          Continue
        </Button>
      </DialogActions>
    </>
  );

  const renderConfirmStep = () => (
    <>
      <DialogContent>
        {/* Summary of selections */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Organizations & Niches
          </Typography>
          {selectedOrgs
            .filter((org) => selectedNicheIds[org.organizationId])
            .map((org) => {
              const nicheId = selectedNicheIds[org.organizationId]!;
              return (
                <Box key={org.organizationId} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>{org.name}</strong> - {org.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Niche: {getNicheName(org.organizationId, nicheId)}
                    {org.collectionName
                      ? ` | Collection: ${org.collectionName}`
                      : ""}
                  </Typography>
                </Box>
              );
            })}
        </Box>

        {/* Brand list */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Brands ({addedPlaces.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Review the brands below. Remove any you don't want to include.
          </Typography>
          <Box
            sx={{
              overflowY: "auto",
            }}
          >
            {addedPlaces.map((place) => (
              <Box key={place.id} sx={{ pr: 2 }}>
                <MapListItem
                  place={place}
                  isSelected={false}
                  addPlace={addPlace}
                  isAdded={true}
                />
              </Box>
            ))}
          </Box>
        </Box>

        <Alert message={message} severity={severity} closeAlert={closeAlert} />
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={() => setStep("select")}>
          Back
        </Button>
        <LoadingButton
          loading={submitLoading}
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={addedPlaces.length === 0}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </>
  );

  const renderCompleteStep = () => (
    <>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            textAlign: "center",
          }}
        >
          <Box
            component="i"
            className="fa-solid fa-circle-check"
            sx={{ fontSize: 64, mb: 2, color: "green.main" }}
          />
          <Typography variant="h5" gutterBottom>
            Upload started successfully
          </Typography>
          <Typography color="text.secondary">
            {addedPlaces.length} brand{addedPlaces.length !== 1 ? "s" : ""}{" "}
            submitted. Would you like to search a new location?
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={() => onSuccess(false)}>
          Close
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={() => onSuccess(true)}
        >
          Search New Location
        </Button>
      </DialogActions>
    </>
  );

  const getTitle = () => {
    if (step === "select") return `Pitch to Brands (${addedPlaces.length})`;
    if (step === "confirm") return "Confirm Upload";
    return "Upload Complete";
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { height: "100vh" } }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h4">{getTitle()}</Typography>
      </DialogTitle>
      {step === "select" && renderSelectStep()}
      {step === "confirm" && renderConfirmStep()}
      {step === "complete" && renderCompleteStep()}
    </Dialog>
  );
}
